import { PostAttachmentType } from '__generated__/graphql/legacy/graphql';
import TrackingEvent, { TrackingProperty } from 'constants/trackingEvents';
import useFileOperations from 'hooks/useFileOperations';
import { Control, FieldErrors, FieldValue, useController, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ACCEPTED_FILE_TYPES } from 'utils/constants';
import { validateFiles } from 'utils/fileHelper';

import React, { useEffect, useState } from 'react';

import { Button, InlineNotification } from '@thinkific/chalk/react-components';
import { Image, Paperclip, Smiley, TextAa, VideoCamera } from '@thinkific/chalk/react-icons';

import { BubbleMenu, EditorContent } from '@tiptap/react';

import { AttachmentPreview } from 'components/AttachmentPreview/AttachmentPreview';
import AttachmentVideoInput from 'components/AttachmentVideoInput/AttachmentVideoInput';
import { useCommunityContext } from 'components/CommunityContext';
import { FormValues as PostFormValues } from 'components/PostForm/PostForm';
import { FormValues as ReplyEditorFormValues } from 'components/ReplyEditorContext/ReplyEditorContext';
import { useGlobalState } from 'components/new/providers/GlobalStateProvider';

import { Attachment } from 'schema/Post/types';

import { useMentionSearch } from '../hooks/useEditor/Extensions/Mentions/useMentionSearch';
import { Content, useEditor } from '../hooks/useEditor/useEditor';
import ActionButton from './ActionButton';
import AttachmentInput from './Actions/AttachmentInput/AttachmentInput';
import EmojiPicker from './Actions/EmojiPicker/EmojiPicker';
import MenuBar from './Actions/MenuBar';
import * as css from './DocumentEditor.css';

type AllFormValues = PostFormValues | ReplyEditorFormValues;

type DocumentEditorProps = {
  placeholder?: string;
  isEditing?: boolean;
  cancelEdit?: () => void;
  control: Control<AllFormValues>;
  errors: FieldErrors<AllFormValues>;
  setValue: (name: string, value: unknown) => void;
  onSubmit?: () => void;
  isSubmitting?: boolean;
  spaceId?: string;
  postType: 'post' | 'reply';
};

export default function DocumentEditor({
  placeholder,
  isEditing = false,
  cancelEdit,
  control,
  errors,
  setValue,
  onSubmit,
  isSubmitting,
  spaceId,
  postType,
}: DocumentEditorProps) {
  const { t } = useTranslation();
  const [videoInputOpen, setVideoInputOpen] = useState(false);
  const { handleSearch } = useMentionSearch();
  const { trackEvent } = useCommunityContext();
  const { isPostToolbarVisible, setIsPostToolbarVisible } = useGlobalState();
  const [error, setError] = useState<string | null>(null);
  const { field: bodyField } = useController({ control, name: 'body' });
  const { isUploading, uploadFiles, generatePreviews } = useFileOperations();
  const bodyFieldValue = useWatch({ control, name: 'body' });
  const attachments = useWatch({ control, name: 'attachments' });
  const { getDraftPost } = useGlobalState();

  useEffect(() => {
    for (const key in errors) {
      if (key === 'body') {
        const message = errors[key]?.jsonContent?.message || errors[key]?.plainText?.message;
        setError(message as string);
      } else if (errors[key]?.message) {
        setError(errors[key].message as string);
      }
    }
  }, [errors]);

  useEffect(() => {
    // If we're editing and there's a video attachment, open the video input
    if (isEditing && attachments?.some((att) => att.attachmentType === PostAttachmentType.Video)) {
      setVideoInputOpen(true);
    }
  }, [isEditing, attachments]);

  useEffect(() => {
    if (!isSubmitting && attachments.length === 0) {
      setVideoInputOpen(false);
    }
  }, [isSubmitting, attachments]);

  const handleBodyChange = (value: Content, callback: (fieldValue: FieldValue<Body>) => void) => {
    callback({
      jsonContent: value.json,
      plainText: value.text,
    });
  };
  const { editor } = useEditor({
    content:
      (spaceId && postType === 'post' && !isEditing && getDraftPost(spaceId)?.body?.jsonContent) ||
      bodyFieldValue?.jsonContent ||
      undefined,
    onBlur: (value: Content) => handleBodyChange(value, bodyField.onChange),
    onChange: (value: Content) => {
      handleBodyChange(value, bodyField.onChange);
      setError(null);
    },
    onMentionsSearch: (search: string) => handleSearch(search, spaceId),
    version: 'editable',
    placeholder,
    className: css.editorContent,
    watchedAtttributes: [spaceId],
  });

  useEffect(() => {
    if (!isSubmitting && !bodyFieldValue?.jsonContent && editor) {
      editor.commands.clearContent();
    }
  }, [bodyFieldValue?.jsonContent, isSubmitting]);

  const trackFormatting = (type: string) => {
    trackEvent(TrackingEvent.FORMATTING_CLICKED, {
      [TrackingProperty.FORMATTING_TYPE]: type,
    });
  };

  const trackAttachment = (type: string) => {
    trackEvent(TrackingEvent.ATTACHMENT_CLICKED, {
      [TrackingProperty.ATTACHMENT]: type,
    });
  };

  const handleSubmit = () => {
    onSubmit?.();
  };

  const onFileSelected = async (files: File[]) => {
    const currentAttachments = control._formValues.attachments;
    setError(null);
    const validationError = validateFiles(files, currentAttachments?.length || 0);
    if (validationError) {
      setError(validationError);
      return;
    }

    const previews = generatePreviews(files);

    setValue('attachments', [...currentAttachments, ...previews]);

    const filesToUpload = files.map((file, index) => ({
      file,
      tempId: previews[index].tempId,
    }));

    const { attachments: uploadedAttachments, error: uploadError } =
      await uploadFiles(filesToUpload);

    if (uploadError) {
      setError(uploadError);
    } else {
      // Replace the previews with the permanent attachments
      const existingAttachments = control._formValues.attachments;
      const updatedAttachments = existingAttachments.map((att) => {
        const found = uploadedAttachments.find((a) => a.tempId === att.tempId);
        if (found) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { tempId, ...rest } = found;
          return rest;
        }
        return att;
      });
      setValue('attachments', updatedAttachments);
    }
  };

  const removeAttachment = (index: number) => {
    setValue(
      'attachments',
      attachments.filter((_, i) => i !== index)
    );
  };

  const onVideoSelected = (attachment: Attachment) => {
    setValue('attachments', [...attachments, attachment]);
  };

  const closeVideoInput = () => {
    // remove video from attachments if it exists
    setValue(
      'attachments',
      attachments.filter((attachment) => attachment.attachmentType !== PostAttachmentType.Video)
    );
    setVideoInputOpen(false);
  };

  const updateAltText = (altText: string, index: number) => {
    const currentAttachments = control._formValues.attachments;
    setValue(
      'attachments',
      currentAttachments.map((attachment, i) =>
        i === index ? { ...attachment, altText } : attachment
      )
    );
  };

  useEffect(() => {
    if (isEditing || postType === 'reply') return;
    const draftPost = spaceId && getDraftPost(spaceId);
    if (draftPost) {
      setValue('attachments', draftPost.attachments);
      if (draftPost.body) {
        setValue('body', draftPost.body);
      }
    }
  }, [spaceId]);

  return (
    <div className={css.document}>
      {editor && (
        <MenuBar
          className={!isPostToolbarVisible ? css.withoutToolbar : undefined}
          editor={editor}
          onActionClick={(action) => trackFormatting(action)}
        />
      )}
      {/* @note: BubbleMenu needs a parent div so it doesn't cause an error on unmount in TipTap v2. Fixed in v3 */}
      {editor && !isPostToolbarVisible && (
        <div>
          <BubbleMenu className={css.bubbleMenu} editor={editor}>
            <MenuBar editor={editor} onActionClick={(action) => trackFormatting(action)} />
          </BubbleMenu>
        </div>
      )}
      <div className={css.content}>
        <EditorContent className={css.editorContent} editor={editor} />
        {error && (
          <InlineNotification
            a11yImportance="assertive"
            actions={[]}
            description={error}
            title="Error"
            variant="danger"
            dismissible
            onDismiss={() => setError(null)}
          />
        )}
        {videoInputOpen && (
          <AttachmentVideoInput
            attachmentUrl={
              attachments.find((att) => att.attachmentType === PostAttachmentType.Video)?.url
            }
            onChange={onVideoSelected}
            onClose={closeVideoInput}
          />
        )}
        {attachments.length > 0 && (
          <div className={css.attachmentContainer}>
            {attachments.map((attachment, index) => (
              <AttachmentPreview
                attachment={attachment}
                attachmentType={attachment.attachmentType}
                key={attachment.id}
                t={t}
                onAltTextChange={(altText: string) => updateAltText(altText, index)}
                onRemove={() => removeAttachment(index)}
              />
            ))}
          </div>
        )}
      </div>
      <div className={css.footerToolbar}>
        <div className={css.actions}>
          <ActionButton
            arialLabel={
              t('components-documentEditor-menuBar_showToolbar', 'Show Toolbar') as string
            }
            dataQa="post-editor__show-toolbar"
            disabled={false}
            icon={TextAa}
            isActive={isPostToolbarVisible}
            onClick={() => {
              trackFormatting('textFormatting');
              setIsPostToolbarVisible(!isPostToolbarVisible);
            }}
          />
          <EmojiPicker
            icon={Smiley}
            onClick={() => {
              trackFormatting('emoji');
            }}
            onSelect={(emoji) => {
              editor?.chain().focus().insertContent(emoji).run();
            }}
          />
          <span className={css.separator}>|</span>
          {[Image, Paperclip].map((Icon) => (
            <AttachmentInput
              accept={ACCEPTED_FILE_TYPES}
              ariaControls="attachment-file-picker__input"
              icon={Icon}
              key={Icon.displayName}
              label={t('components-documentEditor-attachToPost', `Attach ${Icon.displayName}`, {
                type: Icon.displayName,
              })}
              multiple
              onBtnClicked={() => {
                trackAttachment(Icon.displayName === 'Image' ? 'image' : 'file');
              }}
              onFileSelected={onFileSelected}
            />
          ))}
          <ActionButton
            arialLabel={t('components-documentEditor-attachToPost', 'Attach video', {
              type: 'video',
            })}
            dataQa="post-editor__video"
            disabled={videoInputOpen}
            icon={VideoCamera}
            isActive={videoInputOpen}
            onClick={() => {
              trackAttachment('video');
              setVideoInputOpen(true);
            }}
          />
        </div>
        <div className={css.submissions}>
          {isEditing && (
            <Button size="sm" type="button" variant="default" onClick={cancelEdit}>
              {t('Cancel', 'Cancel')}
            </Button>
          )}
          <Button
            dataQa="post-form__submit"
            disabled={isUploading}
            isLoading={isSubmitting}
            size="sm"
            type="submit"
            variant="primary"
            onClick={handleSubmit}
          >
            {isEditing
              ? t('components-richTextEditor-save', 'Save')
              : t('components-richTextEditor-post', 'Post')}
          </Button>
        </div>
      </div>
    </div>
  );
}
